import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import './ChartLayout.css';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import PopulationGrowth from './PopulationGrowth';
import PopulationGrowthComparison from './PopulationGrowthComparison';

Chart.register(...registerables);

interface Props {
  location: string;
  geofips: number | null;
  preferredState?: string | null;
  yearStart?: number;
  yearEnd?: number;
}

type Row = {
  geofips: number;
  location_name: string;
  county: string | null;
  state: string;
  geography_type: 'state' | 'county' | 'place' | string;
  year: number;
  housing_units: number | null;
  num_employees: number | null;
};

export const HousingToEmployees: React.FC<Props> = ({
  location,
  geofips,
  preferredState = null,
  yearStart = 2012,
  yearEnd = 2023
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataRows, setDataRows] = useState<Row[]>([]);
  const [populationSeries, setPopulationSeries] = useState<{ year: number; population: number }[]>([]);
  const [activeView, setActiveView] = useState<string>('overview');
  const [activeChart, setActiveChart] = useState<'ratio' | 'projection' | 'comparison'>('ratio');

  const years = useMemo(
    () => Array.from({ length: (yearEnd - yearStart + 1) }, (_, i) => yearStart + i),
    [yearStart, yearEnd]
  );

  useEffect(() => {
    const fetchData = async () => {
      // Add early return if location is not set
      if (!location || location === '') {
        console.log('No location set yet, waiting...');
        setLoading(false);
        return;
      }
      
      if (!geofips) {
        setError('A geofips is required to render this chart.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('Fetching housing_to_employees data for location:', location, 'geofips:', geofips);
        console.log('Year range:', yearStart, 'to', yearEnd);
        
        let allData = null;
        let allErr = null;
        
        // Check if this is California state (geofips 6) - prioritize state query
        const isCaliforniaState = geofips === 6 || (location && location.toLowerCase() === 'california');
        
        if (isCaliforniaState) {
          console.log('Detected California state - querying state data directly');
          const { data: stateData, error: stateErr } = await supabaseClient
            .from('housing_to_employees')
            .select('*')
            .eq('geography_type', 'state')
            .eq('geofips', 6)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (stateErr) {
            console.error('Error fetching California state data:', stateErr);
            allErr = stateErr;
          }
          
          console.log('California state query result:', stateData?.length || 0, 'rows');
          if (stateData && stateData.length > 0) {
            console.log('Found California state data:', stateData.length, 'rows');
            console.log('Sample state data:', stateData[0]);
            allData = stateData;
          } else {
            console.log('No California state data found');
            allData = [];
          }
        } else {
          // Try place (city) data first, then county, then state
          let query = supabaseClient
            .from('housing_to_employees')
            .select('*')
            .eq('geography_type', 'place')
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          // For cities, prioritize location_name match over geofips
          if (location) {
            console.log('Querying place data by location_name:', location);
            query = query.ilike('location_name', `%${location}%`);
          } else if (geofips) {
            console.log('Querying place data by geofips:', geofips);
            query = query.eq('geofips', geofips);
          }
          
          const { data: placeData, error: placeErr } = await query;
          
          if (placeErr) {
            console.error('Error fetching place data:', placeErr);
          }
          
          console.log('Place query result:', placeData?.length || 0, 'rows');
          if (placeData && placeData.length > 0) {
            console.log('Found place (city) data:', placeData.length, 'rows');
            console.log('Sample place data:', placeData[0]);
            allData = placeData;
          } else {
            console.log('No place data found, trying county data...');
            
            // Try county data
            let countyQuery = supabaseClient
              .from('housing_to_employees')
              .select('*')
              .eq('geography_type', 'county')
              .gte('year', yearStart)
              .lte('year', yearEnd)
              .order('year', { ascending: true });
            
            if (geofips) {
              console.log('Querying county data by geofips:', geofips);
              countyQuery = countyQuery.eq('geofips', geofips);
            } else if (location) {
              console.log('Querying county data by location_name:', location);
              countyQuery = countyQuery.ilike('location_name', `%${location}%`);
            }
            
            const { data: countyData, error: countyErr } = await countyQuery;
            
            if (countyErr) {
              console.error('Error fetching county data:', countyErr);
            }
            
            console.log('County query result:', countyData?.length || 0, 'rows');
            if (countyData && countyData.length > 0) {
              console.log('Found county data:', countyData.length, 'rows');
              console.log('Sample county data:', countyData[0]);
              allData = countyData;
            } else {
              console.log('No county data found, trying state data...');
              
              // Try state data
              let stateQuery = supabaseClient
                .from('housing_to_employees')
                .select('*')
                .eq('geography_type', 'state')
                .gte('year', yearStart)
                .lte('year', yearEnd)
                .order('year', { ascending: true });
              
              if (geofips) {
                console.log('Querying state data by geofips:', geofips);
                stateQuery = stateQuery.eq('geofips', geofips);
              } else if (location) {
                console.log('Querying state data by location_name:', location);
                stateQuery = stateQuery.ilike('location_name', `%${location}%`);
              }
              
              const { data: stateData, error: stateErr } = await stateQuery;
              
              if (stateErr) {
                console.error('Error fetching state data:', stateErr);
              }
              
              console.log('State query result:', stateData?.length || 0, 'rows');
              if (stateData && stateData.length > 0) {
                console.log('Found state-level data:', stateData.length, 'rows');
                console.log('Sample state data:', stateData[0]);
                allData = stateData;
              } else {
                console.log('No data found for any geography type');
                allData = [];
              }
              allErr = stateErr; // Assign the last error encountered
            }
          }
        }

        if (allErr) {
          console.error('Error fetching data:', allErr);
          throw allErr;
        }

        console.log('Total data found:', allData?.length || 0, 'rows');
        if (allData && allData.length > 0) {
          console.log('Sample data:', allData[0]);
          console.log('Geography types:', Array.from(new Set(allData.map(r => r.geography_type))));
          console.log('All data rows:', allData);
        } else {
          console.warn(`No housing to employees data found for geofips ${geofips} (${location})`);
        }
        
        // Use the data exactly as returned from the query - no additional filtering
        setDataRows(allData || []);
        setLoading(false);
      } catch (e: any) {
        console.error('Error loading housing_to_employees:', e);
        setError(e.message || 'Failed to load housing to employees data');
        setLoading(false);
      }
    };

    fetchData();
  }, [geofips, yearStart, yearEnd, location]);

  // Fetch population series for projection view (smart narrative and key metrics)
  useEffect(() => {
    if (!location) return;
    const fetchPopulation = async () => {
      try {
        const { data, error: popError } = await supabaseClient
          .from('population')
          .select('year, population')
          .eq('location_name', location)
          .gte('year', 1990)
          .lte('year', 2045)
          .order('year', { ascending: true });
        if (popError) return;
        const rows = (data || []).map((r: any) => ({ year: Number(r.year), population: Number(r.population) || 0 }));
        setPopulationSeries(rows);
      } catch {
        setPopulationSeries([]);
      }
    };
    fetchPopulation();
  }, [location]);

  const latestPopulationMetric = useMemo(() => {
    if (populationSeries.length === 0) return null;
    return populationSeries[populationSeries.length - 1];
  }, [populationSeries]);

  const historicalGrowthMetric = useMemo(() => {
    if (populationSeries.length < 2) return null;
    const base = populationSeries.find((p) => p.year === 1990) ?? populationSeries[0];
    const last = populationSeries[populationSeries.length - 1];
    if (!base.population || base.population <= 0) return null;
    return ((last.population - base.population) / base.population) * 100;
  }, [populationSeries]);

  const projectedGrowthTo2045Metric = useMemo(() => {
    if (populationSeries.length < 2) return null;
    const first = populationSeries[0];
    const last = populationSeries[populationSeries.length - 1];
    if (!first.population || !last.population || first.population <= 0) return null;
    const n = last.year - first.year;
    if (n <= 0) return null;
    const annualRate = Math.pow(last.population / first.population, 1 / n) - 1;
    const yearsTo2045 = 2045 - last.year;
    if (yearsTo2045 <= 0) return null;
    const projected2045 = last.population * Math.pow(1 + annualRate, yearsTo2045);
    return ((projected2045 - last.population) / last.population) * 100;
  }, [populationSeries]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    if (loading || dataRows.length === 0) return null;
    
    const years = dataRows.map(r => r.year).filter(y => !isNaN(y));
    if (years.length === 0) return null;
    
    const latestYear = Math.max(...years);
    const latestRow = dataRows.find(r => r.year === latestYear);
    
    if (!latestRow || 
        latestRow.housing_units === null || 
        latestRow.housing_units === undefined ||
        latestRow.num_employees === null || 
        latestRow.num_employees === undefined) {
      return null;
    }
    
    const housingUnits = Number(latestRow.housing_units);
    const employees = Number(latestRow.num_employees);
    
    if (isNaN(housingUnits) || isNaN(employees)) {
      return null;
    }
    
    const ratio = employees > 0 
      ? (housingUnits / employees).toFixed(2)
      : 'N/A';
    
    return {
      housingUnits: housingUnits,
      employees: employees,
      ratio: ratio,
      latestYear: latestYear
    };
  }, [loading, dataRows]);

  // Build the chart after data loads
  useEffect(() => {
    if (activeChart !== 'ratio') {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
      return;
    }

    if (loading || dataRows.length === 0) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    console.log('Building chart with dataRows:', dataRows);
    console.log('Sample row:', dataRows[0]);

    // Prepare data for housing units and employees
    const housingData = years.map(year => {
      const row = dataRows.find(r => r.year === year);
      const value = row && row.housing_units !== null ? row.housing_units : null;
      if (value !== null) {
        console.log(`Year ${year}: housing_units = ${value}`);
      }
      return value;
    });

    const employeesData = years.map(year => {
      const row = dataRows.find(r => r.year === year);
      const value = row && row.num_employees !== null ? row.num_employees : null;
      if (value !== null) {
        console.log(`Year ${year}: num_employees = ${value}`);
      }
      return value;
    });

    console.log('Housing data array:', housingData);
    console.log('Employees data array:', employeesData);

    const chartTitle = `Housing Units and Employees - ${location || 'Selected Location'}`;

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: [
          {
            label: 'Housing Units',
            data: housingData,
            borderColor: '#3b82f6', // Blue
            backgroundColor: '#3b82f633',
            borderWidth: 2,
            spanGaps: true,
            tension: 0.25,
            pointRadius: 3
          },
          {
            label: 'Employees',
            data: employeesData,
            borderColor: '#ef4444', // Red
            backgroundColor: '#ef444433',
            borderWidth: 2,
            spanGaps: true,
            tension: 0.25,
            pointRadius: 3
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
          y: {
            title: { display: true, text: 'Count' },
            beginAtZero: true,
            ticks: {
              callback: (v: any) => {
                if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
                if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                return v.toString();
              }
            }
          },
          x: {
            title: { display: true, text: 'Year' }
          }
        },
        plugins: {
          title: standardTitle(chartTitle),
          legend: standardLegend,
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val != null ? val.toLocaleString() : 'N/A'}`;
              }
            }
          }
        }
      }
    });
  }, [activeChart, loading, years, dataRows, location]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Housing_to_Employees');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    const rows: Array<Record<string, any>> = dataRows.map(row => ({
      year: row.year,
      location: row.location_name,
      county: row.county,
      state: row.state,
      geography_type: row.geography_type,
      housing_units: row.housing_units,
      num_employees: row.num_employees
    }));
    downloadDataAsCSV(rows, formatFilename(location, 'Housing_to_Employees_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading housing to employees data for {location || 'selected location'}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Location: {location || 'Not set'}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Geofips: {geofips || 'Not set'}</div>
      </div>
    );
  }
  
  // If no location is set or no data found, don't show anything
  if (!location || (!loading && dataRows.length === 0)) {
    return null;
  }

  const ratioChartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Key metrics display
  const ratioKeyMetricsDisplay = keyMetrics && keyMetrics.housingUnits !== undefined && keyMetrics.employees !== undefined ? (
    <KeyMetrics>
      <KeyMetric
        value={keyMetrics.housingUnits.toLocaleString()}
        label={`Housing Units (${keyMetrics.latestYear})`}
        color="#3b82f6"
      />
      <KeyMetric
        value={keyMetrics.employees.toLocaleString()}
        label={`Employees (${keyMetrics.latestYear})`}
        color="#ef4444"
      />
      <KeyMetric
        value={keyMetrics.ratio}
        label="Housing Units per Employee"
        color="#10b981"
      />
    </KeyMetrics>
  ) : null;

  const projectionKeyMetricsDisplay = latestPopulationMetric && (historicalGrowthMetric !== null || projectedGrowthTo2045Metric !== null) ? (
    <KeyMetrics>
      <KeyMetric
        value={latestPopulationMetric.population.toLocaleString()}
        label={`Population (${latestPopulationMetric.year})`}
        color="#1e3a8a"
      />
      {historicalGrowthMetric !== null && (
        <KeyMetric
          value={`${historicalGrowthMetric > 0 ? '+' : ''}${historicalGrowthMetric.toFixed(1)}%`}
          label="Historical growth (1990–present)"
          color={historicalGrowthMetric >= 0 ? '#10b981' : '#ef4444'}
        />
      )}
      {projectedGrowthTo2045Metric !== null && (
        <KeyMetric
          value={`${projectedGrowthTo2045Metric > 0 ? '+' : ''}${projectedGrowthTo2045Metric.toFixed(1)}%`}
          label="Projected growth (to 2045)"
          color={projectedGrowthTo2045Metric >= 0 ? '#059669' : '#dc2626'}
        />
      )}
    </KeyMetrics>
  ) : (
    <KeyMetrics>
      <KeyMetric value="—" label="Population" color="#1e3a8a" />
      <KeyMetric value="—" label="Historical growth (1990–present)" color="#10b981" />
      <KeyMetric value="—" label="Projected growth (to 2045)" color="#059669" />
    </KeyMetrics>
  );

  const projectionOverview = (
    <OverviewContent
      title="Population Projection Overview"
      description="This chart shows population from 1990 to present and a projection to 2045. Population trends inform housing, mobility, energy demand, and long-term climate adaptation planning."
    >
      {latestPopulationMetric && (historicalGrowthMetric !== null || projectedGrowthTo2045Metric !== null) ? (
        <p>
          {historicalGrowthMetric != null && (
            <>From {populationSeries.find((p) => p.year === 1990) ? 1990 : populationSeries[0]?.year} to {latestPopulationMetric.year}, {location} experienced {historicalGrowthMetric > 0 ? 'growth' : 'a decline'} of {historicalGrowthMetric > 0 ? '+' : ''}{historicalGrowthMetric.toFixed(1)}% (historical growth to present). </>
          )}
          Current population is {latestPopulationMetric.population.toLocaleString()}.
          {projectedGrowthTo2045Metric != null && ` Based on the observed trend, projected growth to 2045 is ${projectedGrowthTo2045Metric > 0 ? '+' : ''}${projectedGrowthTo2045Metric.toFixed(1)}%.`}
          {' '}Population trends directly affect housing demand, transportation, energy use, and climate adaptation planning.
        </p>
      ) : (
        <p>This chart displays population growth trends and projections for the selected location, showing historical population counts from 1990 to present and projected future growth to 2045. Population trends directly impact infrastructure needs, housing demand, transportation, and climate planning.</p>
      )}
    </OverviewContent>
  );

  const projectionRelevance = (
    <RelevanceContent
      title="Relevance to Climate Action"
      description="Population growth directly impacts climate action planning by influencing infrastructure needs, resource consumption, transportation demand, and housing requirements."
    >
      <KeyMetrics>
        <KeyMetric value="Growth-driven demand" label="Energy & water use" color="#1e3a8a" />
        <KeyMetric value="Land use pressure" label="Housing & zoning" color="#10b981" />
        <KeyMetric value="Travel activity" label="Transport emissions" color="#f59e0b" />
      </KeyMetrics>
      <div className="methodology-details-simple">
        <h5>Infrastructure Planning</h5>
        <p>Growing populations require expanded infrastructure including transportation networks, energy systems, and water and wastewater facilities.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Resource Management</h5>
        <p>Population growth increases demand for energy, water, and transportation services, making efficiency and clean energy critical.</p>
      </div>
    </RelevanceContent>
  );

  const projectionEquity = (
    <EquityContent
      title="Equity Considerations"
      description="Population growth patterns can create or exacerbate inequities in access to services, housing, transportation, and climate resilience resources."
    >
      <KeyMetrics>
        <KeyMetric value="Affordability risk" label="Housing cost pressure" color="#1e3a8a" />
        <KeyMetric value="Service gaps" label="Infrastructure strain" color="#dc2626" />
        <KeyMetric value="Exposure" label="Climate vulnerability" color="#f59e0b" />
      </KeyMetrics>
      <div className="methodology-details-simple">
        <h5>Housing Affordability</h5>
        <p>Population growth often increases housing demand and costs, potentially displacing vulnerable populations.</p>
      </div>
      <div className="methodology-details-simple">
        <h5>Service Access</h5>
        <p>Rapid growth can strain services and infrastructure, leaving some communities underserved.</p>
      </div>
    </EquityContent>
  );

  const projectionMethodology = (
    <MethodologyContent
      title="Methodology"
      description="Population data comes from the US Census Bureau and American Community Survey, providing annual estimates for historical analysis and projections."
      dataSources={[
        'US Census Bureau population estimates (annual)',
        'American Community Survey 5-year estimates (demographics)',
        'Census Decennial Census data (baseline counts)'
      ]}
      methodology={[
        'Annual population estimates represent official counts between decennial censuses',
        'Population change calculated as percentage difference from baseline year (2010)',
        'Future projections based on historical trends'
      ]}
    />
  );

  const comparisonKeyMetricsDisplay = (
    <KeyMetrics>
      <KeyMetric value="0%" label="Baseline year index" color="#1e3a8a" />
      <KeyMetric value="Two series" label={`${location} vs California`} color="#10b981" />
      <KeyMetric value="Shared years" label="Aligned timeline" color="#f59e0b" />
    </KeyMetrics>
  );

  const comparisonOverview = (
    <OverviewContent
      title="Population Growth Overview"
      description="This chart shows population growth trends for the selected location and California using the first shared year as baseline."
    >
      <p>The comparison uses percent change from the first shared year so both series start at 0%.</p>
    </OverviewContent>
  );

  const comparisonMethodology = (
    <MethodologyContent
      title="Methodology"
      description="Population data comes from the US Census Bureau and American Community Survey."
      dataSources={[
        'US Census Bureau population estimates (annual)',
        'American Community Survey 5-year estimates (demographics)'
      ]}
      methodology={[
        'Population indexed to the first shared year between location and California',
        'Percent change = (population / baseline population - 1) × 100',
        'Chart includes years where both location and California data are available'
      ]}
    />
  );

  const isRatioView = activeChart === 'ratio';
  const isProjectionView = activeChart === 'projection';
  const isComparisonView = activeChart === 'comparison';

  const chartContent = isProjectionView
    ? <PopulationGrowth location={location} />
    : isComparisonView
      ? <PopulationGrowthComparison location={location} />
      : ratioChartContent;

  const keyMetricsDisplay = isRatioView ? ratioKeyMetricsDisplay : isProjectionView ? projectionKeyMetricsDisplay : isComparisonView ? comparisonKeyMetricsDisplay : null;

  const overviewContent = isProjectionView
    ? projectionOverview
    : isComparisonView
      ? comparisonOverview
      : (
        <OverviewContent
          title="Jobs & Housing Balance"
          description="This chart compares housing units and employees over time to assess jobs-housing balance."
        >
          <div>
            {keyMetrics && keyMetrics.housingUnits !== undefined && keyMetrics.employees !== undefined ? (
              <p>
                As of {keyMetrics.latestYear}, there are {keyMetrics.housingUnits.toLocaleString()} housing units in {location} and {keyMetrics.employees.toLocaleString()} employees. 
                This is a ratio of {keyMetrics.ratio}, meaning there are approximately {keyMetrics.ratio} housing units per employee in {location}.
              </p>
            ) : (
              <p>This chart shows the relationship between housing units and employment in the area over time.</p>
            )}
          </div>
        </OverviewContent>
      );

  const relevanceContent = isProjectionView
    ? projectionRelevance
    : (
      <RelevanceContent
        title="Policy Relevance"
        description="Jobs-housing balance is a core land-use and transportation signal for climate planning."
      >
        <KeyMetrics>
          <KeyMetric value={keyMetrics?.ratio || '—'} label="Units per employee" color="#1e3a8a" />
          <KeyMetric value={keyMetrics?.latestYear ? keyMetrics.latestYear.toString() : '—'} label="Latest year" color="#10b981" />
          <KeyMetric value="RHNA alignment" label="Policy fit" color="#f59e0b" />
        </KeyMetrics>
        <div>
          <p>An imbalance, such as too few homes near job centers, can lead to longer commutes and higher emissions. This is especially challenging in areas with limited affordable housing, pushing workers farther from employment and worsening congestion and pollution. Similarly, residents of bedroom communities with no employment opportunities must commute to other jurisdictions for work.</p>
          <p>California's Regional Housing Needs Allocation (RHNA) and Housing Element Law require local governments to plan for housing growth in proportion to employment trends. Policies like SB 35 and AB 2011 streamline approvals for infill housing near jobs, while Government Code Section 65890.1 provides guidance on measuring and addressing jobs-housing fit. Jobs-housing would go beyond the jobs-housing balance to measure the extent to which the types and affordability of housing in a specific area match the needs of the local workforce.</p>
          <p>Analyzing this ratio helps cities adopt zoning and land use strategies that reduce emissions, lower commuting costs, and promote equitable, compact, and sustainable communities.</p>
        </div>
      </RelevanceContent>
    );

  const equityContent = isProjectionView
    ? projectionEquity
    : (
      <EquityContent
        title="Equity Considerations"
        description="When housing near jobs is scarce or unaffordable, commute burdens and pollution exposure often shift to lower-income households."
      >
        <KeyMetrics>
          <KeyMetric value="Commute burden" label="Cost & time" color="#dc2626" />
          <KeyMetric value="Exposure risk" label="Pollution impacts" color="#f59e0b" />
          <KeyMetric value="Access gap" label="Transit & jobs" color="#1e3a8a" />
        </KeyMetrics>
        <div>
          <p>This jobs-housing mismatch disproportionately affects communities of color and historically marginalized groups, who may also face limited access to reliable transit. Without an equity lens, climate policies, like promoting transit-oriented development or reducing car dependence, can unintentionally increase burdens on disadvantaged populations.</p>
          <p>By analyzing the housing-to-jobs ratio with equity in mind, local governments can target underserved areas, expand affordable housing near employment, and design climate strategies that reduce existing social and economic disparities.</p>
        </div>
      </EquityContent>
    );

  const methodologyContent = isProjectionView
    ? projectionMethodology
    : isComparisonView
      ? comparisonMethodology
      : (
        <MethodologyContent
          title="Data Sources and Methodology"
          description="Housing units come from ACS 5-year estimates; employee counts come from ZIPCode and County Business Pattern datasets."
          dataSources={[
            'US Census American Community Survey (ACS) 5-Year Estimates - Housing Units',
            'ZIPCode Business Pattern (2012-2017) - Employee counts',
            'County Business Pattern (2018-2023) - Employee counts',
            'Census relationship file for mapping ZIP Code to City'
          ]}
          methodology={[
            'Number of employees is mapped from ZIP Code to City based on relationship file from Census',
            'Housing units data comes from ACS 5-Year Estimates',
            'Ratio calculated as housing units divided by employees',
            'Time series analysis from 2012-2023 where data available',
            'Data filtered by geography type (place, county, or state) and location'
          ]}
        />
      );

  return (
    <ChartLayout
      title="Housing, Population & Employment Trends"
      key={`housing-employees-${location}`}
      description={`${location} • Source: US Census ACS 5-Year Estimates & ZIPCode/County Business Patterns`}
      chartType="line"
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={setActiveView}
      chartSelector={{
        value: activeChart,
        onChange: (value: string) => setActiveChart(value as 'ratio' | 'projection' | 'comparison'),
        options: [
          { value: 'ratio', label: 'Housing to Employees Ratio' },
          { value: 'projection', label: 'Population Projection' },
          { value: 'comparison', label: 'Population Growth' }
        ]
      }}
      showRelevanceEquity={!isComparisonView}
      layout="side-by-side"
      onDownloadPNG={
        isProjectionView
          ? () => {
              const canvas = document.querySelector('#population-growth-chart canvas') as HTMLCanvasElement;
              if (canvas) {
                const link = document.createElement('a');
                link.download = `${location.replace(/\s+/g, '_')}_Population_Projection.png`;
                link.href = canvas.toDataURL();
                link.click();
              }
            }
          : isComparisonView
            ? () => {
                const canvas = document.querySelector('#population-growth-comparison-chart canvas') as HTMLCanvasElement;
                if (canvas) {
                  const link = document.createElement('a');
                  link.download = `${location.replace(/\s+/g, '_')}_Population_Growth.png`;
                  link.href = canvas.toDataURL();
                  link.click();
                }
              }
            : handleDownloadPNG
      }
      onDownloadCSV={isProjectionView || isComparisonView ? undefined : handleDownloadCSV}
      pngFilename={
        isProjectionView
          ? formatFilename(location, 'Population_Projection')
          : isComparisonView
            ? formatFilename(location, 'Population_Growth')
            : formatFilename(location, 'Housing_to_Employees')
      }
      csvFilename={
        isProjectionView || isComparisonView
          ? formatFilename(location, 'Population_Data')
          : formatFilename(location, 'Housing_to_Employees_Data')
      }
      enableComments={true}
      location={location}
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      equityContent={equityContent}
      methodologyContent={methodologyContent}
    />
  );
};

export default HousingToEmployees;

